import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import ExternalAccessApi from '../../../../api/external-access-api';
import OrganizationApi from '../../../../api/organization-api';
import SandboxStats from '../../components/SandboxStats';
import PostmanUI from '../../components/PostmanUI';
import apiEndpointsData from '../../data/api-endpoints.json';
import './developer-access-page.scss';
import router from "../../../../shared/top-level-declarations/history";
import Card from '../../../../shared/components/dashboard-layout/components/card/cardComponent';
import Button from '../../../../shared/components/dashboard-layout/components/button/buttonComponent';

const DeveloperAccessPage = () => {
  const { userInfo } = useSelector((state) => state.userStore);
  const organizationId = useSelector(
    (state) => state?.userStore?.userInfo?._organizations?.[0] || ""
  );

  const [orgDetails, setOrgDetails] = useState(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgFetched, setOrgFetched] = useState(false);

  // Mode state (sandbox or live)
  const [mode, setMode] = useState('sandbox');

  // Live mode API key state
  const [liveApiKey, setLiveApiKey] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveGenerating, setLiveGenerating] = useState(false);
  const [liveError, setLiveError] = useState(null);

  // Sandbox mode API key state
  const [sandboxApiKey, setSandboxApiKey] = useState(null);
  const [sandboxLoading, setSandboxLoading] = useState(false);
  const [sandboxGenerating, setSandboxGenerating] = useState(false);
  const [sandboxError, setSandboxError] = useState(null);

  // Sandbox stats state
  const [sandboxStats, setSandboxStats] = useState(null);
  const [sandboxStatsLoading, setSandboxStatsLoading] = useState(false);
  const [sandboxStatsError, setSandboxStatsError] = useState(null);

  // Common state
  const [copied, setCopied] = useState(false);
  const [activeEndpoint, setActiveEndpoint] = useState('ping');
  const [endpoints] = useState(apiEndpointsData.endpoints);

  // Webhook state (Live mode only)
  const [webhook, setWebhook] = useState(null);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookError, setWebhookError] = useState(null);
  const [isCreatingWebhook, setIsCreatingWebhook] = useState(false);
  const [isEditingWebhook, setIsEditingWebhook] = useState(false);
  const [webhookForm, setWebhookForm] = useState({
    webhookUrl: '',
    healthCheckUrl: ''
  });
  const [webhookSaving, setWebhookSaving] = useState(false);

  // Derived state for current mode
  const apiKey = mode === 'live' ? liveApiKey : sandboxApiKey;
  const loading = mode === 'live' ? liveLoading : sandboxLoading;
  const generating = mode === 'live' ? liveGenerating : sandboxGenerating;
  const error = mode === 'live' ? liveError : sandboxError;

  const hasValidServiceAccess = React.useMemo(() => {
    const value = orgDetails?.hasServiceAccessUntil;
    if (!value) return false;
    const ts = new Date(value).getTime();
    if (Number.isNaN(ts)) return false;
    return ts > Date.now();
  }, [orgDetails]);

  const isLiveModeRestricted = orgFetched && !orgLoading && !hasValidServiceAccess;

  useEffect(() => {
    if (!isLiveModeRestricted) return;
    if (mode === 'live') setMode('sandbox');
  }, [isLiveModeRestricted, mode]);

  useEffect(() => {
    let isMounted = true;
    const fetchOrg = async () => {
      if (!organizationId) {
        if (isMounted) {
          setOrgDetails(null);
          setOrgFetched(true);
        }
        return;
      }
      try {
        if (isMounted) setOrgLoading(true);
        const response = await OrganizationApi.private.fetchOrganization(organizationId);
        const data = response?.data?.data;
        if (isMounted) setOrgDetails(data || null);
      } catch (err) {
        console.error('Error fetching organization details:', err);
        if (isMounted) setOrgDetails(null);
      } finally {
        if (isMounted) {
          setOrgLoading(false);
          setOrgFetched(true);
        }
      }
    };

    fetchOrg();
    fetchSandboxApiKey();
    fetchSandboxStats();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!orgFetched) return;
    if (isLiveModeRestricted) return;

    fetchLiveApiKey();
    fetchWebhook();
  }, [orgFetched, isLiveModeRestricted]);

  useEffect(() => {
    // Fetch sandbox stats when mode changes to sandbox
    if (mode === 'sandbox') {
      fetchSandboxStats();
    }
  }, [mode]);

  // Fetch Live API key
  const fetchLiveApiKey = async () => {
    try {
      setLiveLoading(true);
      setLiveError(null);
      const response = await ExternalAccessApi.private.getApiKey();
      if (response?.data?.data?.apiKey) {
        setLiveApiKey(response.data.data.apiKey);
      }
      setLiveLoading(false);
    } catch (err) {
      setLiveLoading(false);
      if (err.response && err.response.status === 404) {
        console.log('No live API key found for this organization');
      } else {
        setLiveError('Failed to fetch API key. Please try again.');
        console.error('Error fetching live API key:', err);
      }
    }
  };

  // Fetch Sandbox API key
  const fetchSandboxApiKey = async () => {
    try {
      setSandboxLoading(true);
      setSandboxError(null);
      const response = await ExternalAccessApi.private.getSandboxApiKey();
      if (response?.data?.data?.apiKey) {
        setSandboxApiKey(response.data.data.apiKey);
      }
      setSandboxLoading(false);
    } catch (err) {
      setSandboxLoading(false);
      if (err.response && err.response.status === 404) {
        console.log('No sandbox API key found for this organization');
      } else {
        setSandboxError('Failed to fetch sandbox API key. Please try again.');
        console.error('Error fetching sandbox API key:', err);
      }
    }
  };

  // Fetch Sandbox Stats
  const fetchSandboxStats = async () => {
    try {
      setSandboxStatsLoading(true);
      setSandboxStatsError(null);
      const response = await ExternalAccessApi.private.getSandboxStats();
      if (response?.data?.data) {
        setSandboxStats(response.data.data);
      }
      setSandboxStatsLoading(false);
    } catch (err) {
      setSandboxStatsLoading(false);
      if (err.response && err.response.status === 404) {
        console.log('No sandbox stats found for this organization');
      } else {
        setSandboxStatsError('Failed to fetch sandbox statistics. Please try again.');
        console.error('Error fetching sandbox stats:', err);
      }
    }
  };

  // Generate Live API key
  const generateLiveApiKey = async () => {
    try {
      setLiveGenerating(true);
      setLiveError(null);
      const response = await ExternalAccessApi.private.createApiKey();
      if (response?.data?.data?.apiKey) {
        setLiveApiKey(response.data.data.apiKey);
      }
      setLiveGenerating(false);
    } catch (err) {
      setLiveGenerating(false);
      setLiveError('Failed to generate API key. Please try again.');
      console.error('Error generating live API key:', err);
    }
  };

  // Generate Sandbox API key
  const generateSandboxApiKey = async () => {
    try {
      setSandboxGenerating(true);
      setSandboxError(null);
      const response = await ExternalAccessApi.private.createSandboxApiKey();
      if (response?.data?.data?.apiKey) {
        setSandboxApiKey(response.data.data.apiKey);
      }
      setSandboxGenerating(false);
    } catch (err) {
      setSandboxGenerating(false);
      setSandboxError('Failed to generate sandbox API key. Please try again.');
      console.error('Error generating sandbox API key:', err);
    }
  };

  // Generate API key based on current mode
  const generateApiKey = () => {
    if (mode === 'live') {
      generateLiveApiKey();
    } else {
      generateSandboxApiKey();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(apiKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const fetchWebhook = async () => {
    try {
      setWebhookLoading(true);
      setWebhookError(null);
      const response = await ExternalAccessApi.private.getWebhook();
      if (response?.data?.data?.webhookUrl) {
        setWebhook(response.data.data);
        setWebhookForm({
          webhookUrl: response.data.data.webhookUrl || '',
          healthCheckUrl: response.data.data.healthCheckUrl || ''
        });
      }
      setWebhookLoading(false);
    } catch (err) {
      setWebhookLoading(false);
      // If it's a 404 error, it just means no webhook exists yet, so don't show an error
      if (err.response && err.response.status === 404) {
        console.log('No webhook found for this organization');
      } else {
        setWebhookError('Failed to fetch webhook configuration. Please try again.');
        console.error('Error fetching webhook:', err);
      }
    }
  };

  const handleWebhookFormChange = (field, value) => {
    setWebhookForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const createWebhook = async () => {
    try {
      setWebhookSaving(true);
      setWebhookError(null);
      
      const payload = {
        webhookUrl: webhookForm.webhookUrl?.trim(),
        ...(webhookForm.healthCheckUrl && webhookForm.healthCheckUrl.trim()
          ? { healthCheckUrl: webhookForm.healthCheckUrl.trim() }
          : {}),
      };

      const response = await ExternalAccessApi.private.createWebhook(payload);
      
      if (response?.data?.data) {
        setWebhook(response.data.data);
        setIsCreatingWebhook(false);
        setWebhookForm({
          webhookUrl: '',
          healthCheckUrl: ''
        });
      }
      setWebhookSaving(false);
    } catch (err) {
      setWebhookSaving(false);
      setWebhookError('Failed to create webhook configuration. Please try again.');
      console.error('Error creating webhook:', err);
    }
  };

  const updateWebhook = async () => {
    try {
      setWebhookSaving(true);
      setWebhookError(null);
      
      const payload = {
        ...(webhookForm.webhookUrl && webhookForm.webhookUrl.trim()
          ? { webhookUrl: webhookForm.webhookUrl.trim() }
          : {}),
        ...(webhookForm.healthCheckUrl && webhookForm.healthCheckUrl.trim()
          ? { healthCheckUrl: webhookForm.healthCheckUrl.trim() }
          : {}),
      };

      const response = await ExternalAccessApi.private.updateWebhook(webhook?._id, payload);
      
      if (response?.data?.data) {
        setWebhook(response.data.data);
        setIsEditingWebhook(false);
      }
      setWebhookSaving(false);
    } catch (err) {
      setWebhookSaving(false);
      setWebhookError('Failed to update webhook configuration. Please try again.');
      console.error('Error updating webhook:', err);
    }
  };

  const deleteWebhook = async () => {
    if (!window.confirm('Are you sure you want to delete the webhook configuration? This action cannot be undone.')) {
      return;
    }

    try {
      setWebhookSaving(true);
      setWebhookError(null);
      
      await ExternalAccessApi.private.deleteWebhook(webhook?._id);
      
      setWebhook(null);
      setWebhookForm({
        webhookUrl: '',
        healthCheckUrl: ''
      });
      setIsEditingWebhook(false);
      setWebhookSaving(false);
    } catch (err) {
      setWebhookSaving(false);
      setWebhookError('Failed to delete webhook configuration. Please try again.');
      console.error('Error deleting webhook:', err);
    }
  };

  const startEditWebhook = () => {
    setWebhookForm({
      webhookUrl: webhook.webhookUrl || '',
      healthCheckUrl: webhook.healthCheckUrl || ''
    });
    setIsEditingWebhook(true);
    setWebhookError(null);
  };

  const cancelCreateWebhook = () => {
    setWebhookForm({
      webhookUrl: '',
      healthCheckUrl: ''
    });
    setIsCreatingWebhook(false);
    setWebhookError(null);
  };

  const cancelEditWebhook = () => {
    setWebhookForm({
      webhookUrl: '',
      healthCheckUrl: ''
    });
    setIsEditingWebhook(false);
    setWebhookError(null);
  };

  const downloadApiEndpointsJson = () => {
    const dataStr = JSON.stringify(apiEndpointsData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'jely-api-endpoints.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (<>
    <div className="developer-access-container">
        <div className="pageHeaderTitle">
          <div className="pageTitle">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M7 8L3 11.6923L7 16M17 8L21 11.6923L17 16M14 4L10 20" stroke="var(--text-primary)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
            Developer Access
          </div>
          <div className="mode-toggle">
            <button
              className={`mode-toggle-btn ${mode === 'sandbox' ? 'active' : ''}`}
              onClick={() => setMode('sandbox')}
            >
              Sandbox
            </button>
            {!isLiveModeRestricted && (
              <button
                className={`mode-toggle-btn ${mode === 'live' ? 'active' : ''}`}
                onClick={() => setMode('live')}
              >
                Live
              </button>
            )}
          </div>
        </div>

        {mode !== 'live' && (<>
          <Card
            className="mb-4"
            header={<div className="card-header-with-badge">
              Sandbox Mode
              <span className={`mode-badge ${sandboxStats == null ? '' : sandboxStats.canSend ? 'live' : 'limit-reached'}`}>
                {sandboxStats == null ? '' : sandboxStats.canSend ? 'Active' : 'Limit Reached'}
              </span>
            </div>}
            subheader={"Use this environment to test your integrations safely. Webhook configuration is not available in sandbox mode."}
            notes={<>
              <span>
                Live mode is available with a subscription —{' '}
                <button
                  type="button"
                  className="btn btn-link p-0 align-baseline"
                  onClick={() => router.navigate('/settings')}
                >
                  you can begin a subscription here
                </button>
                .
              </span>
              <br></br>
              {sandboxStats != null && !sandboxStats.canSend &&
                <span>Sandbox message limit reached. Contact support to increase your limit.</span>
              }
            </>}
          >
            <SandboxStats
              stats={sandboxStats}
              loading={sandboxStatsLoading}
              error={sandboxStatsError}
            />
          </Card>
        </>)}

        <Card
          className={"api-key-card mb-4"}
          header={<div className="card-header-with-badge">
            API Keys
            <span className={`mode-badge ${mode}`}>{mode === 'live' ? 'Live' : 'Sandbox'}</span>
          </div>
          }
          subheader={"Use this API key to authenticate your requests to the Jely API."}
          notes={mode === 'sandbox'
            ? 'This is your sandbox API key. Use it for testing purposes only.'
            : "This is your organization's live API key. Keep it secure and don't share it publicly."
          }
        >
          {loading ? (
              <div className="loading-indicator">Loading {mode} API key...</div>
            ) : (
              <>
                {apiKey ? (
                  <div className="api-key-display">
                    <div className="api-key-value">
                      <div className="codeBlock"><code>{apiKey}</code></div>
                      <button 
                        className="customButton small" 
                        onClick={copyToClipboard}
                        disabled={copied}
                      >
                        <span>{copied ? '✓ Copied!' : 'Copy Key'}</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="no-api-key">
                    <p>No {mode} API key found for your organization.</p>
                    <button 
                      className="generate-button" 
                      onClick={generateApiKey}
                      disabled={generating}
                    >
                      {generating ? 'Generating...' : `Generate ${mode === 'live' ? 'Live' : 'Sandbox'} API Key`}
                    </button>
                  </div>
                )}
              </>
            )}
            {error && <div className="error-message">{error}</div>}
        </Card>


        {mode === 'live' && 
          <Card
            className={"webhook-card mb-4"}
            header={"Webhook Configuration"}
            subheader={"Configure a webhook endpoint to receive notifications when new messages arrive."}
            notes={""}
          >
            {webhookLoading ? (
                <div className="loading-indicator">Loading webhook configuration...</div>
              ) : (
                <>
                  {webhook && !isEditingWebhook ? (
                    <div className="webhook-display">
                      <Card
                        className={"webhook-info mb-2"}
                        neutralButtonLabel={
                          <>
                            <i className="mdi mdi-pencil"></i>
                            <span>Edit Webhook</span>
                          </>
                        }
                        neutralButtonAction={startEditWebhook}
                        negativeButtonLabel={
                          <>
                            <i className="mdi mdi-trash-can"></i>
                            <span>Edit Webhook</span>
                          </>
                        }
                        negativeButtonAction={deleteWebhook}
                      >
                        <div className="webhook-field">
                          <label className="mb-0">Webhook URL:</label>
                          <div className="codeBlock"><code>{webhook.webhookUrl}</code></div>
                        </div>
                        {webhook.healthCheckUrl && (
                          <div className="webhook-field">
                            <label className="mb-0">Health Check URL:</label>
                            <div className="codeBlock"><code>{webhook.healthCheckUrl}</code></div>
                          </div>
                        )}
                        <div className="webhook-status">
                          <span className={`status-badge ${webhook.status === 'active' ? 'active' : 'inactive'}`}>
                            {webhook.status}
                          </span>
                        </div>
                      </Card>
                    </div>
                  ) : (webhook && isEditingWebhook) || isCreatingWebhook ? (
                    <div className="webhook-display">
                      <Card
                          className={"webhook-info mb-2"}
                          cancelButtonLabel={ <span>Cancel</span> }
                          cancelButtonAction={isEditingWebhook ? cancelEditWebhook : cancelCreateWebhook}
                          positiveButtonLabel={
                            webhookSaving ? 
                            (isEditingWebhook ? 'Updating...' : 'Creating...') : 
                            (isEditingWebhook ? 'Update Webhook' : 'Create Webhook')
                          }
                          positiveButtonAction={isEditingWebhook ? updateWebhook : createWebhook}
                        >
                        <div className="webhook-field">
                          <label htmlFor="webhookUrl" className="mb-0">Webhook URL: <span className="text-danger">*</span></label>
                          <input
                            type="url"
                            id="webhookUrl"
                            value={webhookForm.webhookUrl}
                            onChange={(e) => handleWebhookFormChange('webhookUrl', e.target.value)}
                            placeholder="https://your-domain.com/webhook"
                            className="form-control"
                            required
                          />
                          <small>This URL will receive POST requests with message data when new messages arrive.</small>
                        </div>
                        
                        <div className="webhook-field">
                          <label htmlFor="healthCheckUrl" className="mb-0">Health Check URL</label>
                          <input
                            type="url"
                            id="healthCheckUrl"
                            value={webhookForm.healthCheckUrl}
                            onChange={(e) => handleWebhookFormChange('healthCheckUrl', e.target.value)}
                            placeholder="https://your-domain.com/health"
                            className="form-control"
                          />
                          <small>Optional: URL for health checks to ensure your webhook is operational.</small>
                        </div>
                      </Card>
                    </div>
                  ) : (
                    <div className="no-webhook">
                      <p>No webhook configured for your organization.</p>
                      <button 
                        className="generate-button" 
                        onClick={() => setIsCreatingWebhook(true)}
                      >
                        Configure Webhook
                      </button>
                    </div>
                  )}
                </>
              )}
            
            {webhookError && <div className="error-message">{webhookError}</div>}




              <div className="mt-3">
                <div className="cardSubSection">
                  <div className="cardSubSectionHeader">
                    <h4>Webhook Payload</h4>
                    <div>Your webhook will receive POST requests with the following structure:</div>
                  </div>
                  <pre className="codeBlock mt-2">
                    <code>
  {`{
    "_id": "6730c9...",
    "direction": "outgoing",
    "platformOn": "whatsapp",
    "msgType": "notification",
    "message": "Hello from Relai",
    "attachments": [],
    "metadata": { "templateName": "welcome_v1" },
    "status": "sent",
    "createdAt": "2025-08-26T18:48:00.000Z",
    "raw": { "provider": "twilio", "sid": "SM..." }
  }`}
                      </code>
                    </pre>
                  </div>
              </div>
          </Card>
        }

        <Card
          className={"api-doc-card mb-4"}
          header={"API Documentation"}
          subheader={"Explore the available endpoints and learn how to integrate with Jely."}
          notes={""}
        >
          <div className="api-doc-actions">
            <button 
              className="customButton small download-json-btn" 
              onClick={downloadApiEndpointsJson}
            >
              <i className="mdi mdi-download"></i>
              <span>Download API Endpoints JSON</span>
            </button>
            <small className="text-muted">Download complete API documentation to share with AI copilots and development tools</small>
          </div>

          <div className="cardSubSection">
              <div className="cardSubSectionHeader">
                <h4>Base URL</h4>
                <div>All API endpoints are relative to the following base URL:</div>
                <div className="codeBlock">
                  <code>
                    {process.env.REACT_APP_API_BASE_URL.replace('api/v1', 'external/v1')}
                  </code>
                </div>
            </div>
          </div>

          <div className="cardSubSection">
              <div className="cardSubSectionHeader">
                <h4>Authentication</h4>
                <div>Include your API key in the request header</div>
                <div className="codeBlock">
                  <code>
                    {process.env.REACT_APP_API_BASE_URL.replace('api/v1', 'external/v1')}
                  </code>
                </div>
            </div>
          </div>


          <div className="api-docs-container">
            <div className="api-sidebar">
              <h4>Available Endpoints</h4>
              <ul className="api-nav">
                {endpoints.map(endpoint => (
                  <li 
                    key={endpoint.id}
                    className={activeEndpoint === endpoint.id ? 'active' : ''}
                    onClick={() => setActiveEndpoint(endpoint.id)}
                  >
                    <span className="endpoint-method">{endpoint.method}</span>
                    <span className="endpoint-path">{endpoint.path.split('/').slice(-1)[0]}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="api-content">
              {endpoints.map(endpoint => (
                activeEndpoint === endpoint.id && (
                  <div className="api-endpoint-details" key={endpoint.id}>
                    <div className="endpoint-header">
                      <span className={`endpoint-method ${endpoint.method.toLowerCase()}`}>{endpoint.method}</span>
                      <span className="endpoint-full-path">{endpoint.path}</span>
                    </div>
                    <div className="endpoint-description">
                      <p>{endpoint.description}</p>
                    </div>
                    
                    <div className="endpoint-section">
                      <h5>Request</h5>
                      <pre className="codeBlock">
                        <code>
                          {`${endpoint.method} ${endpoint.path}

                          Headers:
                          ${Object.entries(endpoint.request.headers).map(([key, value]) => `${key}: ${value}`).join('\n')}`}
                                                      {endpoint.request.body && `

                          Body:
                          ${JSON.stringify(endpoint.request.body, null, 2)}`}
                        </code>
                      </pre>
                    </div>
                    
                    <div className="endpoint-section">
                      <h5>Response</h5>
                      <pre className="codeBlock">
                        <code>
                          {JSON.stringify(endpoint.response.success.content, null, 2)}
                        </code>
                      </pre>
                    </div>
                    
                    {endpoint.parameters && endpoint.parameters.length > 0 && (
                      <div className="endpoint-section">
                        <h5>Parameters</h5>
                        <table className="parameters">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Type</th>
                              <th>Required</th>
                              <th>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {endpoint.parameters.map(param => (
                              <tr key={param.name}>
                                <td>{param.name}</td>
                                <td>{param.type}</td>
                                <td>{param.required ? 'Yes' : 'No'}</td>
                                <td>{param.description}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    
                    <div className="endpoint-section">
                      <h5>Status Codes</h5>
                      <table className="status-codes">
                        <tbody>
                          {endpoint.statusCodes.map(status => (
                            <tr key={status.code}>
                              <td>{status.code}</td>
                              <td>{status.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>
        </Card>

        <Card
          className={"mb-4"}
          header={"API Playground"}
          subheader={"Test your API endpoints directly from the browser with our interactive Postman-like interface."}
        >
          <PostmanUI 
            endpoints={endpoints}
            apiKey={apiKey}
            mode={mode}
          />
        </Card>

    </div>
  </>);
};

export default DeveloperAccessPage;
